import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';

import { Layout, Menu, message, FloatButton, Button, ConfigProvider, theme, Spin, App as AntApp } from 'antd';
import {
  RobotOutlined,
  PhoneOutlined,
  ThunderboltOutlined,
  MessageOutlined,
  ConsoleSqlOutlined,
  UserOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  SettingOutlined,
  QuestionCircleOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  ExperimentOutlined,
  SunOutlined,
  MoonOutlined,
  RocketOutlined,
  NodeIndexOutlined,
  ForkOutlined,
  ApartmentOutlined
} from '@ant-design/icons';
import TemplateManager from './components/TemplateManager';
import MappingManager from './components/MappingManager';
import FunctionManager from './components/FunctionManager';
import ChatHistory from './components/ChatHistory';
import LiveChatLog from './components/LiveChatLog';
import UserMappingManager from './components/UserMappingManager';
import GroupMappingManager from './components/GroupMappingManager';
import GroupReplyRulesManager from './components/GroupReplyRulesManager';
import MasterBotConfigManager from './components/MasterBotConfigManager';
import BotManager from './components/BotManager';
import LiveTest from './components/LiveTest';
import UserManual from './components/UserManual';
import UserManager from './components/UserManager';
import apiClient from './api';
import Login from './components/Login';
import AccountManager from './components/AccountManager';
import WhatsAppTemplateManager from './components/WhatsAppTemplateManager';
import BroadcastManager from './components/BroadcastManager';
import WhatsAppFlowManager from './components/WhatsAppFlowManager';
import InboundRoutingManager from './components/InboundRoutingManager';
import HumanAgentTerminal from './components/HumanAgentTerminal';
import KnowledgeStudio from './components/KnowledgeStudio';
import CommandCentre from './components/CommandCentre';
import Onboarding from './components/Onboarding';
import LeadList from './components/LeadList';
import './App.css';

const { Content, Sider } = Layout;
const THEME_STORAGE_KEY = 'agent-frontend-theme';
const LIGHT_START_HOUR = 6;
const LIGHT_END_HOUR = 18;

function getIsDarkByTime() {
  const hour = new Date().getHours();
  return hour < LIGHT_START_HOUR || hour >= LIGHT_END_HOUR;
}

const MENU_ITEMS = [
  { type: 'group', label: <div className="menu-group-title">Agent Factory</div>, children: [
    // { key: '/bot-creator', icon: <RobotOutlined />, label: 'Intelligent Bot Creator' },
    { key: '/functions', icon: <ThunderboltOutlined />, label: 'Custom Functions' },
    { key: '/agent-templates', icon: <RobotOutlined />, label: 'Agent Templates' },
    { key: '/master-config', icon: <SettingOutlined />, label: 'Master Config' },
  ]},
  { type: 'group', label: <div className="menu-group-title">Connectivity</div>, children: [
    { key: '/number-mappings', icon: <PhoneOutlined />, label: 'Number Mappings' },
    { key: '/user-mappings', icon: <UserOutlined />, label: 'User Mappings' },
    { key: '/inbound-routing', icon: <ForkOutlined />, label: 'Inbound Routing' },
    { key: '/group-mappings', icon: <TeamOutlined />, label: 'Group Mappings' },
    { key: '/reply-rules', icon: <ClockCircleOutlined />, label: 'Reply Rules' },
  ]},
  { type: 'group', label: <div className="menu-group-title">Messaging</div>, children: [
    { key: '/wa-templates', icon: <ThunderboltOutlined />, label: 'WA Templates' },
    { key: '/wa-flows', icon: <NodeIndexOutlined />, label: 'WA Flows' },
    { key: '/broadcasts', icon: <RocketOutlined />, label: 'Broadcasts' },
  ]},
  { type: 'group', label: <div className="menu-group-title">Intelligence</div>, children: [
    { key: '/command-centre', icon: <ThunderboltOutlined />, label: 'Command Centre' },
  ]},
  { type: 'group', label: <div className="menu-group-title">Analytics & Tools</div>, children: [
    { key: '/leads', icon: <TeamOutlined />, label: 'Leads' },
    { key: '/human-terminal', icon: <UserOutlined />, label: 'Human agent console' },
    { key: '/chat-history', icon: <MessageOutlined />, label: 'Chat History' },
    { key: '/live-logs', icon: <ConsoleSqlOutlined />, label: 'Live Logs' },
    { key: '/live-test', icon: <ExperimentOutlined />, label: 'Live Test' },
    { key: '/knowledge-studio', icon: <ApartmentOutlined />, label: 'Knowledge Studio' },
  ]},
  { type: 'group', label: <div className="menu-group-title">Integrations & System</div>, children: [
    { key: '/settings', icon: <SettingOutlined />, label: 'API Keys & Account' },
    { key: '/team', icon: <TeamOutlined />, label: 'Team Details' },
  ]},
];

function AppContent({ templates, mappings, functions, loading, loadData, account, setAccount, manualVisible, setManualVisible, sidebarCollapsed, setSidebarCollapsed, themeMode, setThemeMode, isDark }) {
  const navigate = useNavigate();


  const location = useLocation();
  const isTerminal = location.pathname === '/human-terminal';

  const common = { loading, onUpdate: loadData };

  return (
    <Layout
      className={`app-layout theme-${isDark ? 'dark' : 'light'} ${sidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'} ${isTerminal ? 'is-terminal' : ''}`}
      hasSider
    >
      <Sider
        className="app-sider"
        collapsible
        collapsed={sidebarCollapsed}
        onCollapse={setSidebarCollapsed}
        onBreakpoint={(broken) => {
          if (broken) setSidebarCollapsed(true);
        }}
        trigger={null}
        breakpoint="lg"
        width={280}
        collapsedWidth={88}
      >
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
          <div className="sider-logo">
            {sidebarCollapsed ? (
              <div className="sider-collapsed-actions">
                <Button
                  type="text"
                  className="sider-expand-btn"
                  icon={<MenuUnfoldOutlined />}
                  onClick={() => setSidebarCollapsed(false)}
                  aria-label="Expand menu"
                />
              </div>
            ) : (
              <>
                <span className="sider-logo-text">🤖 Master Agent</span>
                <div className="sider-actions">
                  <Button
                    type="text"
                    className="sider-collapse-btn"
                    icon={<MenuFoldOutlined />}
                    onClick={() => setSidebarCollapsed(true)}
                    aria-label="Collapse menu"
                  />
                </div>
              </>
            )}
          </div>
          <Menu
            theme={isDark ? 'dark' : 'light'}
            selectedKeys={[location.pathname]}
            mode="inline"
            inlineCollapsed={sidebarCollapsed}
            items={MENU_ITEMS}
            onClick={({ key }) => {
              navigate(key);
              if (window.innerWidth <= 992) setSidebarCollapsed(true);
            }}
            style={{ borderRight: 0, flex: 1, overflowY: 'auto' }}
          />
          <div style={{ padding: sidebarCollapsed ? '16px 8px' : '16px', borderTop: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Button
              type="text"
              icon={themeMode === 'auto' ? <ClockCircleOutlined /> : isDark ? <SunOutlined /> : <MoonOutlined />}
              onClick={() => setThemeMode((m) => (m === 'light' ? 'dark' : m === 'dark' ? 'auto' : 'light'))}
              style={{ width: '100%', textAlign: sidebarCollapsed ? 'center' : 'left', color: isDark ? '#fff' : 'inherit' }}
            >
              {!sidebarCollapsed && 'Toggle Theme'}
            </Button>
            <Button 
              type="text" 
              danger
              icon={<UserOutlined />} 
              onClick={() => {
                localStorage.clear();
                setAccount(null);
                navigate('/');
              }}
              style={{ width: '100%', textAlign: sidebarCollapsed ? 'center' : 'left' }}
            >
              {!sidebarCollapsed && 'Logout'}
            </Button>
          </div>
        </div>
      </Sider>

      <Layout>
        <Content className="app-content">
          <div
            className="sider-backdrop"
            onClick={() => setSidebarCollapsed(true)}
            onKeyDown={(e) => e.key === 'Escape' && setSidebarCollapsed(true)}
            role="button"
            tabIndex={0}
            aria-label="Close menu"
          />
          <div className="content-wrapper">
            <Routes>
              <Route path="/" element={<Navigate to="/agent-templates" replace />} />
              <Route path="/bot-creator" element={<BotManager templates={templates} functions={functions} {...common} />} />

              <Route path="/functions" element={<FunctionManager {...common} />} />
              <Route path="/agent-templates" element={<TemplateManager templates={templates} functions={functions} {...common} />} />
              <Route path="/number-mappings" element={<MappingManager mappings={mappings} templates={templates} {...common} />} />
              <Route path="/user-mappings" element={<UserMappingManager templates={templates} {...common} />} />
              <Route path="/inbound-routing" element={<InboundRoutingManager {...common} />} />
              <Route path="/group-mappings" element={<GroupMappingManager templates={templates} {...common} />} />
              <Route path="/reply-rules" element={<GroupReplyRulesManager templates={templates} {...common} />} />
              <Route path="/master-config" element={<MasterBotConfigManager templates={templates} {...common} />} />
              <Route path="/chat-history" element={<ChatHistory />} />
              <Route path="/live-logs" element={<LiveChatLog />} />
              <Route path="/human-terminal" element={<HumanAgentTerminal />} />
              <Route path="/leads" element={<LeadList accountId={account?._id} apiKey={localStorage.getItem('whatsapp_agent_api_key')} />} />
              <Route path="/live-test" element={<LiveTest />} />
              <Route path="/settings" element={<AccountManager />} />
              <Route path="/wa-templates" element={<WhatsAppTemplateManager />} />
              <Route path="/wa-flows" element={<WhatsAppFlowManager />} />
              <Route path="/broadcasts" element={<BroadcastManager />} />
              <Route path="/knowledge-studio" element={<KnowledgeStudio />} />
              <Route path="/command-centre" element={<CommandCentre />} />
              <Route path="/team" element={<UserManager />} />
              <Route path="*" element={<Navigate to="/agent-templates" replace />} />

            </Routes>

          </div>
        </Content>
      </Layout>

      <FloatButton
        icon={<QuestionCircleOutlined />}
        type="primary"
        className="manual-float-btn"
        style={{ right: 32, bottom: 32, width: 64, height: 64 }}
        tooltip="User Manual & Guide"
        onClick={() => setManualVisible(true)}
      />

      <UserManual 
        visible={manualVisible} 
        onClose={() => setManualVisible(false)} 
        templates={templates}
        functions={functions}
        loading={loading}
        onUpdate={loadData}
        account={account}
      />
    </Layout>
  );
}

function App() {
  const [templates, setTemplates] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manualVisible, setManualVisible] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [themeMode, setThemeMode] = useState(() => {
    try {
      const s = localStorage.getItem(THEME_STORAGE_KEY);
      return s === 'dark' || s === 'auto' ? s : 'light';
    } catch {
      return 'light';
    }
  });
  const [autoIsDark, setAutoIsDark] = useState(getIsDarkByTime);
  const [account, setAccount] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  const isDark = themeMode === 'dark' ? true : themeMode === 'auto' ? autoIsDark : false;

  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, themeMode);
      document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    } catch (e) {}
  }, [themeMode, isDark]);

  useEffect(() => {
    if (themeMode !== 'auto') return;
    const tick = () => setAutoIsDark(getIsDarkByTime());
    const id = setInterval(tick, 60 * 1000);
    return () => clearInterval(id);
  }, [themeMode]);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const key = localStorage.getItem('whatsapp_agent_api_key');
    if (!key) {
      setAuthChecked(true);
      setLoading(false);
      return;
    }

    try {
      const res = await apiClient.get('/accounts/me');
      if (res.data.success) {
        setAccount(res.data.account);
        loadData();
      }

 else {
        localStorage.removeItem('whatsapp_agent_api_key');
      }
    } catch (err) {
      console.error('Auth check failed', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('whatsapp_agent_api_key');
      }
    } finally {
      setAuthChecked(true);
      setLoading(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Load templates
      try {
        const templatesRes = await apiClient.get('/templates');
        if (templatesRes.data.success) setTemplates(templatesRes.data.templates);
      } catch (err) { console.error('Templates fetch failed', err); }

      // Load mappings
      try {
        const mappingsRes = await apiClient.get('/mappings/numbers');
        if (mappingsRes.data.success) setMappings(mappingsRes.data.mappings);
      } catch (err) { console.error('Mappings fetch failed', err); }

      // Load functions
      try {
        const functionsRes = await apiClient.get('/functions');
        if (functionsRes.data.success) setFunctions(functionsRes.data.functions);
      } catch (err) { console.error('Functions fetch failed', err); }

    } catch (error) {
      console.error('Core loadData error:', error);
      message.error('Failed to load partial data');
    } finally {
      setLoading(false);
    }
  };

  if (!authChecked) {
    return (
      <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <ConfigProvider 
      theme={{ 
        algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          colorPrimary: '#6366f1',
          borderRadius: 12,
          fontFamily: "'Inter', sans-serif",
          colorBgContainer: isDark ? '#1e293b' : '#ffffff',
          boxShadow: '0 4px 20px -2px rgba(0, 0, 0, 0.05)',
        },
        components: {
          Card: { borderRadiusLG: 16 },
          Button: { borderRadius: 10, fontWeight: 600 },
          Menu: { itemBorderRadius: 10, itemMarginInline: 8 }
        }
      }}
    >
      <AntApp>
        {!account ? (
          <Login onLoginSuccess={(acc) => {
            setAccount(acc);
            loadData();
          }} />
        ) : !account.onboardingCompleted ? (
          <Onboarding account={account} onComplete={async () => {
            // Optimistic update immediately
            setAccount(a => ({ ...a, onboardingCompleted: true }));
            // Also re-fetch from server to ensure DB flag is synced
            try {
              const res = await apiClient.get('/accounts/me');
              if (res.data.success) setAccount(res.data.account);
            } catch (_) {}
            loadData();
          }} />
        ) : (
          <AppContent
            templates={templates}
            mappings={mappings}
            functions={functions}
            loading={loading}
            loadData={loadData}
            account={account}
            setAccount={setAccount}
            manualVisible={manualVisible}
            setManualVisible={setManualVisible}
            sidebarCollapsed={sidebarCollapsed}
            setSidebarCollapsed={setSidebarCollapsed}
            themeMode={themeMode}
            setThemeMode={setThemeMode}
            isDark={isDark}
          />
        )}
      </AntApp>
    </ConfigProvider>
  );
}

export default App;
